import React from 'react';

import { FormattedMessage } from 'react-intl';

import EditorInfoIcon from '@atlaskit/icon/glyph/info';
import { RovoIcon } from '@atlaskit/logo';
import Lozenge from '@atlaskit/lozenge';
import { Anchor, Box, Flex, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import {
  DEFAULT_AUTOREVIEW_COMMENT_UUIDS,
  isAutoreviewCommentUUID,
} from 'src/components/conversation/src/utils/is-autoreview-comment-uuid';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { useConfigString } from 'src/contexts/statsig-config-provider';

import messages from './autoreview-feedback.i18n';

export const AutoreviewFooter = ({ createdById }: { createdById: string }) => {
  const commentUUIDs = useConfigString(
    StatsigFeatureKeys.autoreviewCommentUuidConfig,
    DEFAULT_AUTOREVIEW_COMMENT_UUIDS
  );
  const isAutoreviewComment = isAutoreviewCommentUUID(
    createdById,
    commentUUIDs
  );

  const isAutoreviewFeedbackEnabled = useStatsigGate(
    StatsigFeatureKeys.enableAutoreviewFeedbackUi
  ) as boolean;

  if (!isAutoreviewComment || !isAutoreviewFeedbackEnabled) {
    return <></>;
  }

  const anchorStyles = xcss({
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    ':hover': {
      textDecoration: 'none',
    },
  });

  // this resize balances this info icon with the Rovo icon on the other side
  const infoIconContainerStyles = xcss({
    transform: 'scale(0.8)',
    paddingTop: 'space.025',
  });

  return (
    <Box paddingBlockStart="space.200" paddingInlineEnd="space.200">
      <Flex justifyContent="space-between" alignItems="center">
        <Flex alignItems="center" gap="space.050">
          <Lozenge>
            <FormattedMessage {...messages.beta} />
          </Lozenge>
          <Box xcss={infoIconContainerStyles}>
            <EditorInfoIcon
              label="Content quality may vary"
              primaryColor={token('color.icon.subtle', '#626F86')}
              size="small"
            />
          </Box>
          <Anchor
            href="https://www.atlassian.com/trust/atlassian-intelligence"
            target="_blank"
            xcss={anchorStyles}
          >
            <Text size="small" color="color.text.subtlest">
              <FormattedMessage {...messages.contentQualityMayVary} />
            </Text>
          </Anchor>
        </Flex>
        <Flex alignItems="center" justifyContent="end" gap="space.050">
          <Text size="small" color="color.text.subtlest">
            Rovo
          </Text>
          <RovoIcon appearance="neutral" size="xsmall" />
        </Flex>
      </Flex>
    </Box>
  );
};
